interface VideoState {
    container: HTMLElement;
    introSrc: string;
    loopSrc: string | null;
    currentVideo: HTMLVideoElement | null;
    started: boolean;
    finishedIntro: boolean;
    startTimer: number | null;
}

function isAppleVideoTarget(): boolean {
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    const vendor = navigator.vendor || '';

    const isIPhoneOrIPod = /iPhone|iPod/i.test(ua);
    const isIPad = /iPad/i.test(ua) || (/MacIntel/i.test(platform) && maxTouchPoints > 1);

    const isMac = /Mac/i.test(platform);
    const isSafari =
        /Apple/i.test(vendor) &&
        !/CriOS|Chrome|Edg|OPR|Firefox/i.test(ua);

    return isIPhoneOrIPod || isIPad || (isMac && isSafari);
}

function createVideo(src: string, loop = false): HTMLVideoElement {
    const video = document.createElement('video');

    video.src = src;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.loop = loop;
    video.preload = 'auto';

    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');

    video.style.position = 'absolute';
    video.style.inset = '0';
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'cover';
    video.style.display = 'block';

    return video;
}

function destroyVideo(video: HTMLVideoElement | null): void {
    if (!video) return;

    video.pause();
    video.removeAttribute('src');
    video.load();
    video.remove();
}

function syncContainerRatio(container: HTMLElement, video: HTMLVideoElement): void {
    const applyRatio = (): void => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
            container.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
        }
    };

    if (video.readyState >= 1) {
        applyRatio();
    } else {
        video.addEventListener('loadedmetadata', applyRatio, { once: true });
    }
}

function waitForFirstFrame(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve) => {
        let done = false;

        const finish = (): void => {
            if (done) return;
            done = true;
            resolve();
        };

        if ('requestVideoFrameCallback' in video) {
            video.requestVideoFrameCallback(() => finish());
            return;
        }

        if (video.readyState >= 2) {
            requestAnimationFrame(() => finish());
            return;
        }

        video.addEventListener(
            'loadeddata',
            () => {
                requestAnimationFrame(() => finish());
            },
            { once: true }
        );

        window.setTimeout(() => finish(), 120);
    });
}

export function initViewportReplaceVideos(selector = '.element-video'): () => void {
    const containers = Array.from(document.querySelectorAll<HTMLElement>(selector));
    const stateMap = new Map<HTMLElement, VideoState>();
    const useMainVideo = isAppleVideoTarget();

    const playSingle = (state: VideoState): void => {
        if (state.started) return;

        state.started = true;

        destroyVideo(state.currentVideo);

        const video = createVideo(state.introSrc, true);
        syncContainerRatio(state.container, video);

        video.style.visibility = 'hidden';
        video.style.zIndex = '1';

        state.currentVideo = video;
        state.container.append(video);

        void video.play()
            .catch(() => {})
            .finally(() => {
                void waitForFirstFrame(video).then(() => {
                    video.style.visibility = 'visible';
                });
            });
    };

    const playLoop = (state: VideoState): void => {
        if (!state.loopSrc) return;

        const prevVideo = state.currentVideo;
        const loopVideo = createVideo(state.loopSrc, true);

        loopVideo.style.visibility = 'hidden';
        loopVideo.style.zIndex = '2';

        if (prevVideo) {
            prevVideo.style.zIndex = '1';
        }

        state.currentVideo = loopVideo;
        state.container.append(loopVideo);

        void loopVideo.play()
            .catch(() => {})
            .finally(() => {
                void waitForFirstFrame(loopVideo).then(() => {
                    loopVideo.style.visibility = 'visible';

                    if (prevVideo) {
                        destroyVideo(prevVideo);
                    }
                });
            });
    };

    const playIntro = (state: VideoState): void => {
        if (state.started) return;

        state.started = true;

        destroyVideo(state.currentVideo);

        const introVideo = createVideo(state.introSrc, false);
        syncContainerRatio(state.container, introVideo);

        introVideo.style.visibility = 'hidden';
        introVideo.style.zIndex = '1';

        state.currentVideo = introVideo;

        introVideo.addEventListener(
            'ended',
            () => {
                if (state.finishedIntro) return;

                state.finishedIntro = true;
                playLoop(state);
            },
            { once: true }
        );

        state.container.append(introVideo);

        void introVideo.play()
            .catch(() => {})
            .finally(() => {
                void waitForFirstFrame(introVideo).then(() => {
                    introVideo.style.visibility = 'visible';
                });
            });
    };

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const state = stateMap.get(entry.target as HTMLElement);
                if (!state) return;

                if (!entry.isIntersecting) return;
                if (state.started) return;
                if (state.startTimer !== null) return;

                state.startTimer = window.setTimeout(() => {
                    state.startTimer = null;

                    if (state.started) return;

                    if (state.loopSrc) {
                        playIntro(state);
                    } else {
                        playSingle(state);
                    }

                    observer.unobserve(state.container);
                }, 120);
            });
        },
        {
            threshold: 0.15,
            rootMargin: '0px 0px -10% 0px',
        }
    );

    containers.forEach((container) => {
        const introMain = container.dataset.videoAMain?.trim() || '';
        const introFallback = container.dataset.videoAFallback?.trim() || '';
        const loopMain = container.dataset.videoBMain?.trim() || '';
        const loopFallback = container.dataset.videoBFallback?.trim() || '';

        const introSrc = useMainVideo ? introMain : introFallback;
        const loopSrcRaw = useMainVideo ? loopMain : loopFallback;
        const loopSrc = loopSrcRaw || null;

        if (!introSrc) return;

        container.style.position = 'relative';
        container.style.overflow = 'hidden';
        container.style.width = '100%';

        const state: VideoState = {
            container,
            introSrc,
            loopSrc,
            currentVideo: null,
            started: false,
            finishedIntro: false,
            startTimer: null,
        };

        stateMap.set(container, state);
        observer.observe(container);
    });

    return () => {
        observer.disconnect();

        stateMap.forEach((state) => {
            if (state.startTimer !== null) {
                window.clearTimeout(state.startTimer);
            }

            destroyVideo(state.currentVideo);
            state.currentVideo = null;
        });

        stateMap.clear();
    };
}